import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, WifiOff, Share as ShareIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const DISMISS_KEY = 'pwa-dismissed-at';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent || '') && !window.MSStream;
const isStandalone = () =>
  (typeof window !== 'undefined' &&
    window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator?.standalone === true;

const canReoffer = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return true;
    return Date.now() - parseInt(raw, 10) > THIRTY_DAYS_MS;
  } catch (_e) { return true; }
};

const PWAInstallPrompt = () => {
  const { isDarkMode } = useTheme();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    if (isStandalone() || !canReoffer()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS never fires beforeinstallprompt — show a Share-hint after 25s.
    let iosTimer = null;
    if (isIOS()) {
      iosTimer = setTimeout(() => {
        setIosHint(true);
        setShowPrompt(true);
      }, 25000);
    }

    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowPrompt(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* noop */ }
  };

  const dismiss = () => {
    setShowPrompt(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* noop */ }
  };

  return (
    <>
      {/* Offline indicator */}
      <AnimatePresence>
        {isOffline && (
          <motion.div initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -50, opacity: 0 }}
            className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center gap-2 py-2 bg-amber-500 text-amber-900 text-sm font-medium" data-testid="offline-banner">
            <WifiOff className="w-4 h-4" /> You&apos;re offline — cached content available
          </motion.div>
        )}
      </AnimatePresence>

      {/* Install prompt */}
      <AnimatePresence>
        {showPrompt && (
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
            className={`fixed top-4 right-4 z-50 w-80 rounded-2xl p-4 shadow-2xl ${isDarkMode ? 'bg-slate-900 border border-white/10' : 'bg-white border border-slate-200'}`}
            data-testid="pwa-install-prompt">
            <button onClick={dismiss} data-testid="pwa-install-dismiss" aria-label="Dismiss install prompt"
              className={`absolute top-3 right-3 ${isDarkMode ? 'text-white/40 hover:text-white/60' : 'text-slate-400 hover:text-slate-600'}`}>
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-rose-400 flex items-center justify-center flex-shrink-0">
                {iosHint ? <ShareIcon className="w-5 h-5 text-white" /> : <Download className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h3 className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Install NeuroHeal
                </h3>
                <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  {iosHint ? 'One-tap access from your home screen' : 'Full offline access + faster load'}
                </p>
              </div>
            </div>
            {iosHint ? (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                In Safari, tap <ShareIcon className="w-3 h-3 inline mx-0.5" /> Share below and then choose{' '}
                <strong>Add to Home Screen</strong>.
              </p>
            ) : (
              <button onClick={handleInstall}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-rose-400 text-white text-sm font-medium"
                data-testid="install-pwa-btn">
                Install App
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PWAInstallPrompt;
